import { Select, Tooltip } from "antd"
import { useEffect, useState } from "react";
import { getRequest } from "../../utils/RequestActions";
import { useSelector } from "react-redux";
import { getTranslated, parseRefAPI, validateAttr, validateRegex } from "../../utils/CommonUtils";
import { applyROAccess } from "../../utils/ValidationUtils";
import { Button, Divider, Input, Space } from 'antd';
import { PlusOutlined, CloseOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { ERROR_RED } from "../../styles/styleUtils";

const CustomCombobox = (props) => {

    const [loading, setLoader] = useState(false)
    const [enumList, setEnumList] = useState([])
    const [value, setValue] = useState(null)

    const [readOnly, setReadOnly] = useState(false)
    const [attrStatus, setAttrStatus] = useState("")

    const [regexpStatus, setRegexpStatus] = useState()
    const childAttr = props.attribute?.[0] || {};
    const regexp = childAttr?.localRegexp;
    const regexpHelp = childAttr?.localRegexpHelp;

    const [textFieldValue, setTextFieldValue] = useState(null)

    const dict = useSelector(state => state.common.dict)

    useEffect(() => { // send data on every change to currentMergedData via handleSchemaDataChange 
        if (value === null && props.value === undefined) {
            setValue(props.defaultValue)
            props.handleSchemaDataChange && props.handleSchemaDataChange(props, props.defaultValue)
        } else {
            setValue(props.value)
        }
    }, [props.value, props.defaultValue])

    useEffect(() => { // make the widget access level based on the props
        setReadOnly(props.isReadOnly || applyROAccess(props.access, props.actionMode))
    }, [props.access, props.actionMode, props.isReadOnly])

    useEffect(() => { //validate on value change
        if (props.validateState) {
            const res = validateAttr(props, value)
            setAttrStatus(res)
        }
    }, [props.validateState, value])

    useEffect(() => {
        if (!props.refApi?.apiUrl && props.enum) {
            const tempEnums = props.enum?.map(option => {
                return {
                    ...option,
                    label: getTranslated(dict, option.label),
                    value: getTranslated(dict, option.value)
                }
            })
            setEnumList(tempEnums)
        }
    }, [props.enum])

    // get data from refAPI 
    useEffect(() => {
        props.refApi?.apiurl && handleRefApi(props.refApi.apiurl)
    }, [props.refApi?.apiUrl])

    const handleRefApi = (apiUrl) => {
        setLoader(true)
        const request = {
            url: parseRefAPI(apiUrl)
        }
        getRequest(request, (response, status) => {
            setLoader(false)
            if (status)
                setEnumList(response)
            else
                setEnumList([])
        })
    }

    const handleSelect = (value) => {
        if (!readOnly) {
            const attrStatus = validateAttr(props, value)
            setAttrStatus(attrStatus)

            setValue(value !== undefined ? value : '')
            props.handleSelect && props.handleSelect(value)
            props.handleSchemaDataChange && props.handleSchemaDataChange(props, value)
        }
    }

    const handleChange = (value) => {
        setValue(value !== undefined ? value : '')
    }

    const handleBlur = () => {
        props.handleBlur && props.handleBlur(value, props.id)
    }

    const handleClearOnDropdown = () => {
        if (!readOnly) {
            const attrStatus = validateAttr(props, value)
            setAttrStatus(attrStatus)

            setValue(value)
            props.handleSelect && props.handleSelect(value)
            props.handleSchemaDataChange && props.handleSchemaDataChange(props, value)
        }
    }

    // custom input data addition to dropdown list 
    const addItem = e => {
        if (!textFieldValue || textFieldValue.trim() === '') return;
        if (regexp && !regexpStatus) return;

        setEnumList([...enumList, { label: textFieldValue, value: textFieldValue }]);
        setTextFieldValue('');
        setRegexpStatus(null)
    };

    const handleTextFieldChange = e => { //custom input regex validation using child attribute's regex
        const val = e.target.value;
        setTextFieldValue(val)

        if (!regexp) {
            setRegexpStatus(null);
            return;
        }
        if (!val || val.trim() === '') {
            setRegexpStatus(null);
            return;
        }
        const isValid = regexp ? validateRegex(regexp, val) : true;
        setRegexpStatus(isValid)
    }

    const renderPopupContent = (menu) => {
        return (
            <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                {regexp && regexpStatus === false && (
                    <Space
                        style={{ padding: '0 8px 4px', color: 'red' }}>
                        {getTranslated(dict, regexpHelp)}
                    </Space>
                )}
                <div style={{ padding: "0 12px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", width: "100%" }}>
                        <Input
                            placeholder={getTranslated(dict, props?.placeholder)}
                            value={textFieldValue}
                            onChange={handleTextFieldChange}
                            onKeyDown={e => e.stopPropagation()}
                            style={{
                                flexBasis: "calc(70% - 6px)",
                                borderColor: regexpStatus === false ? 'red' : undefined
                            }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={addItem}
                            disabled={!textFieldValue || regexp ? regexpStatus !== true : false}
                            style={{ flexBasis: "calc(30% - 6px)" }}
                        >
                            Add item
                        </Button>
                    </div>
                </div>
            </>)
    }

    return <div style={{ display: attrStatus.errorMsg ? "flex" : "" }}
        className={`attr-wrap ${readOnly ? "read-only" : ""}`}>
        <Select
            showSearch
            className={`${props.className} ${props.parentClassName}`}
            value={value}
            placeholder={getTranslated(dict, props?.placeholder)}
            status={attrStatus?.status}
            readOnly={readOnly}
            style={{ width: 300 }}
            popupRender={renderPopupContent}
            options={enumList?.map(item => ({ label: item.label, value: item.value }))}
            loading={loading}
            onSelect={handleSelect}
            onChange={handleChange}
            onBlur={() => handleBlur()}
            onDropdownVisibleChange={open => {
                if (!open) {
                    setTextFieldValue("");
                    setRegexpStatus(null);
                }
            }}
            mode={props.mode || ""}
            tabIndex={readOnly && -1} //keyboard tab key navigation
            maxTagCount={props.maxTagCount || ""}
            filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear={{ clearIcon: <CloseOutlined onClick={handleClearOnDropdown} /> }}
        />
        {attrStatus.errorMsg && (
            <Tooltip title={getTranslated(dict, attrStatus.errorMsg)}>
                <ExclamationCircleFilled style={{ color: ERROR_RED, paddingLeft: "5px" }} />
            </Tooltip>
        )}
    </div>
}
export default CustomCombobox;